import './ReactMixAndMatchCarousel.scss'

import { useState } from 'react'

import { ButtonTypes } from '@cox/core-ui8/dist/Button'
import { useFlexOffers } from '@cox/core-ui8/dist/useFlexOffers'

import { CAROUSEL_DARK_BLUE_ACTIVE_ARROW_BUTTON, CAROUSEL_INACTIVE_ARROW_BUTTON } from '../../../assets/images'
import { getImageSrc } from '../../../utils/local-util'
import { ScreenSizes } from '../../../utils/types'
import ReactButton from '../../ReactButton'
import ReactText from '../../ReactText'
import ActionButton from '../ActionButton'
import CustomDot from '../CustomDot'
import ReactMultiCarouselV2 from '../ReactMultiCarouselV2'
import { ActionButtonTypes, CardState, responsiveMixAndMatchCarouselProps } from '../types'

const ReactMixAndMatchCarousel = (props: any): JSX.Element => {
  const {
    eyebrow,
    title,
    description,
    activePip,
    inActivePip,
    activePipAltText,
    inActivePipAltText,
    ruleExpression = '',
    activeAuthoredIndex,
    mndMlineOptions,
    mndMPlanOptions = [],
    plusIcon,
    plusIconAlt,
    closeIcon,
    closeIconAlt,
    totalMsg,
    carouselId,
    deviceOptionsLink,
    carouselActiveButton,
    carouselDisabledButton,
    carouselActiveButtonAltText = 'Next',
    carouselDisabledButtonAltText = 'Previous',
    isInEditor = false
  } = props
  const flexOffers = useFlexOffers(ruleExpression)

  const initialLineOptionCards = [...mndMlineOptions].map((item, index) => {
    // Set only the first card active by default:
    const initialCardState = index == 0 ? CardState.ACTIVE : CardState.INACTIVE
    return {
      cardState: initialCardState,
      icon: item.optionsIcon,
      text: item.lines,
      lineNumber: index + 1,
      selectedPlanName: mndMPlanOptions[0]?.mndMPlanName,
      currentPrice: mndMPlanOptions[0]?.line1,
      currentScratchPrice: mndMPlanOptions[0]?.scratchPrice,
      planUnit: mndMPlanOptions[0]?.planUnit || '/mo'
    }
  })

  const [lineCards, setLineCards] = useState(initialLineOptionCards)

  const [totals, setTotals] = useState({
    numberOfLines: 0,
    totalPrice: mndMPlanOptions?.length > 0 ? Number(mndMPlanOptions[0]?.line1) : 0,
    totalScratchPrice: mndMPlanOptions?.length > 0 ? Number(mndMPlanOptions[0]?.scratchPrice) : 0
  })

  const [totalForText, setTotalForText] = useState('for')
  const [totalUnitText, setTotalUnitText] = useState('/mo')

  const getCarouselItems = () => {
    const carouselItems = lineCards.map((item: any, index) => {
      const activeLines = totals.numberOfLines + 1

      if (index < activeLines + 1) {
        return (
          <MixAndMatchCarouselSlide
            key={`${item.id}_mandm`}
            cardState={item.cardState}
            icon={item.icon}
            text={item.text}
            lineNumber={item.lineNumber}
            lineCards={lineCards}
            mndMPlanOptions={mndMPlanOptions}
            setLineCards={setLineCards}
            index={index}
            totals={totals}
            setTotals={setTotals}
            plusIcon={plusIcon}
            plusIconAlt={plusIconAlt}
            closeIcon={closeIcon}
            closeIconAlt={closeIconAlt}
            setTotalForText={setTotalForText}
            setTotalUnitText={setTotalUnitText}
          />
        )
      }
    })

    //filters out any empty values
    return carouselItems.filter((elm) => elm)
  }

  const carouselItems = getCarouselItems()

  return (
    <div data-testid={`react-mixandmatch-carousel`} className={`react-mixandmatch-carousel`}>
      <div className='mixandmatch-carousel-container'>
        <div className='react-carousel responsive-size'>
          <ReactMultiCarouselV2
            //custom props
            eyebrow={eyebrow}
            title={title}
            description={description}
            //multicarousel props
            ssr={true}
            partialVisible={true}
            responsive={responsiveMixAndMatchCarouselProps}
            arrows={false}
            renderButtonGroupOutside={true}
            customButtonGroup={
              <ButtonGroup
                numberOfLines={totals.numberOfLines}
                nextButton={carouselActiveButton}
                previousButton={carouselDisabledButton}
                nextButtonAltText={carouselActiveButtonAltText}
                previousButtonAltText={carouselDisabledButtonAltText}
              />
            }
            showDots={true}
            renderDotsOutside={true}
            swipeable={true}
            customDot={
              <CustomDot
                activePip={activePip}
                inActivePip={inActivePip}
                activePipAltText={activePipAltText}
                inActivePipAltText={inActivePipAltText}
              />
            }
            flexOffers={flexOffers}
            activeAuthoredIndex={activeAuthoredIndex}
            transitionDuration={2000}
            isInEditor={isInEditor}
          >
            {carouselItems.length > 0 &&
              carouselItems?.map((item: any) => {
                return (
                  <div className='carousel-item-wrapper' key={JSON.stringify(item.key)}>
                    {item}
                  </div>
                )
              })}
          </ReactMultiCarouselV2>
        </div>
        <div className='react-mix-and-match-carousel-footer-container'>
          <p className='total-summary-text'>
            {totalMsg}{' '}
            {!isNaN(totals.totalPrice) && (
              <span className='line-for-price-text'>
                {totals.numberOfLines + 1} {totals.numberOfLines > 0 ? 'lines' : 'line'} {totalForText}{' '}
                {totals.totalPrice == 0 ? '' : `$${totals.totalPrice.toFixed(2)}${totalUnitText}`}
              </span>
            )}{' '}
            {!isNaN(totals.totalScratchPrice) && (
              <span className='scratch-price-text'>
                {totals.totalPrice == totals.totalScratchPrice ? '' : `$${totals.totalScratchPrice.toFixed(2)}/mo`}
              </span>
            )}
          </p>
          <a className='device-options-link cox-text-interactive2' href={carouselId}>
            {deviceOptionsLink && <ReactText text={deviceOptionsLink} />}
          </a>
        </div>
      </div>
    </div>
  )
}

const MixAndMatchCarouselSlide = (props: any) => {
  const {
    cardState,
    icon,
    text,
    lineNumber,
    lineCards,
    mndMPlanOptions,
    setLineCards,
    index,
    totals,
    setTotals,
    plusIcon,
    plusIconAlt,
    closeIcon,
    closeIconAlt,
    setTotalForText,
    setTotalUnitText
  } = props

  const handleCardClick = (event: any, cardState: any) => {
    // keyCode = 9 for TAB key
    if (event.keyCode !== 9) {
      event.preventDefault()
      if (cardState == CardState.INACTIVE) {
        addLine()
      }
    }
  }

  const planButtons = mndMPlanOptions?.map((planOption: any, planIndex: number) => {
    const buttonType =
      lineCards[index].selectedPlanName == planOption.mndMPlanName ? ButtonTypes.MIXANDMATCH : ButtonTypes.MIXANDMATCHALT

    return (
      <ReactButton
        icon={planOption.planIcon}
        text={planOption.mndMPlanName}
        buttonTypes={buttonType}
        customClickEvent={() => updateLine(planOption.mndMPlanName)}
        key={planOption.mndMPlanName + planIndex}
      />
    )
  })

  const getSelectedPlan = (planName: string) => {
    for (let i = 0; i < mndMPlanOptions?.length; i++) {
      if (mndMPlanOptions[i]?.mndMPlanName == planName) {
        return mndMPlanOptions[i]
      }
    }
  }

  const getPriceForNumberOfLines = (lineNumber: number, selectedPlan: any) => {
    let priceForLine
    switch (lineNumber) {
      case 1:
        priceForLine = selectedPlan.line1
        break
      case 2:
        priceForLine = selectedPlan.line2
        break
      case 3:
        priceForLine = selectedPlan.line3
        break
      case 4:
        priceForLine = selectedPlan.line4
        break
      case 5:
        priceForLine = selectedPlan.line5
        break
      default:
        priceForLine = selectedPlan.scratchPrice
    }
    return priceForLine
  }

  const getTotalPrices = (newLineCards: any) => {
    let totalPrice = 0,
      totalScratchPrice = 0
    for (let i = 0; i < newLineCards.length; i++) {
      if (newLineCards[i].cardState == CardState.ACTIVE) {
        totalPrice = totalPrice + newLineCards[i].currentPrice
        totalScratchPrice = totalScratchPrice + newLineCards[i].currentScratchPrice
      }
    }

    return { totalPrice, totalScratchPrice }
  }

  const getNumberOfLinesWithSamePlan = (newLineCards: any, selectedPlanName: string) => {
    let numberOfLinesWithSamePlan = 0
    for (let i = 0; i < lineCards.length; i++) {
      if (newLineCards[i].cardState == CardState.ACTIVE) {
        if (lineCards[i].selectedPlanName == selectedPlanName) {
          numberOfLinesWithSamePlan++
        }
      }
    }
    return numberOfLinesWithSamePlan
  }

  const updateLineCardsAndTotals = (newLineCards: any, numberOfLines: number) => {
    // Loop through lineCards to update the currentPrice for the lines with the same plan that were impacted:
    for (let i = 0; i < mndMPlanOptions?.length; i++) {
      // For each unique plan:

      // Find number of lines with the same plan:
      const numberOfLinesWithSamePlan = getNumberOfLinesWithSamePlan(newLineCards, mndMPlanOptions[i]?.mndMPlanName)

      // Get selected plan:
      const selectedPlan = getSelectedPlan(mndMPlanOptions[i]?.mndMPlanName)

      // Get the price per line for the selected plan:
      const pricePerLineForPlan = getPriceForNumberOfLines(numberOfLinesWithSamePlan, selectedPlan)

      for (let j = 0; j < newLineCards.length; j++) {
        if (newLineCards[j].selectedPlanName == mndMPlanOptions[i]?.mndMPlanName) {
          newLineCards[j].currentPrice = pricePerLineForPlan
          newLineCards[j].currentScratchPrice = mndMPlanOptions[i]?.scratchPrice
          newLineCards[j].planUnit = mndMPlanOptions[i]?.planUnit || '/mo'
        }
      }
    }

    // Update line cards:
    setLineCards(newLineCards)

    // Calculate totals:
    const priceObj = getTotalPrices(newLineCards)

    // Update totals:
    setTotals({
      numberOfLines: numberOfLines,
      totalPrice: priceObj.totalPrice,
      totalScratchPrice: priceObj.totalScratchPrice
    })

    // Gets all active cards:
    const activeLineCards = []
    for (let i = 0; i < lineCards.length; i++) {
      if (lineCards[i].cardState == CardState.ACTIVE) {
        activeLineCards.push(lineCards[i])
      }
    }

    // Check if user selected mix and matched plans, to update the total summary text
    const isPlansMixedAndMatched = activeLineCards.some(
      (lineCard: any) => lineCard.selectedPlanName !== mndMPlanOptions[0].mndMPlanName
    )
    if (isPlansMixedAndMatched) {
      setTotalForText('for as low as')
    } else setTotalForText('for')

    // Checks if all active cards are Pay As You Gig plan:
    const isAllPayAsYouGig = activeLineCards.every((lineCard) => lineCard.selectedPlanName == 'Pay As You Gig')
    if (isAllPayAsYouGig) {
      setTotalForText('for')
      setTotalUnitText('/GB per mo')
    } else setTotalUnitText('/mo')
  }

  const addLine = () => {
    const newLineCards = [...lineCards]

    // Set card to active:
    newLineCards[totals.numberOfLines + 1].cardState = CardState.ACTIVE

    // Update line cards:
    updateLineCardsAndTotals(newLineCards, totals.numberOfLines + 1)
  }

  const deleteLine = (event: any) => {
    // keyCode 9 = tab key
    if (event.keyCode !== 9) {
      event.preventDefault()

      const newLineCards = [...lineCards]

      // Set card to inactive:
      newLineCards[index].cardState = CardState.INACTIVE

      // Set card default option back to the first option:
      newLineCards[index].selectedPlanName = mndMPlanOptions[0]?.mndMPlanName

      // Update line cards:
      updateLineCardsAndTotals(newLineCards, totals.numberOfLines - 1)
    }
  }

  const updateLine = (selectedMndMPlanName: string) => {
    // Update selected plan name for the line:
    const newLineCards = [...lineCards]
    newLineCards[index].selectedPlanName = selectedMndMPlanName

    // Update line cards:
    updateLineCardsAndTotals(newLineCards, totals.numberOfLines)
  }

  return (
    <div className={`carousel-item-container line-${cardState}`} onClick={(event) => handleCardClick(event, cardState)}>
      {cardState == CardState.ACTIVE && (
        <>
          <div className='line-card-header'>
            <div
              className={`top-container ${index == 0 && `disable-close`} ${
                totals.numberOfLines != index && `disable-close`
              } ${index != 0 && totals.numberOfLines == index && 'close-visible'}`}
            >
              <div className='image-container'>
                <img className='line-image' src={getImageSrc(icon)} alt='line-image' />
              </div>
              {index != 0 && totals.numberOfLines == index && (
                <div
                  className='close-line'
                  onClick={(event) => deleteLine(event)}
                  tabIndex={0}
                  onKeyDown={(event) => deleteLine(event)}
                >
                  <img className='x-blue-icon' src={getImageSrc(closeIcon)} alt={closeIconAlt} />
                </div>
              )}
            </div>
            <p className='line-number-text cox-text-title1-bold'>{text ? `${text}` : `Line ${lineNumber}`}</p>
          </div>

          <div className='plans-container'>{planButtons}</div>

          <div className='price-container'>
            {lineCards[index]?.currentPrice && (
              <p className='price cox-text-title2-bold'>
                ${lineCards[index].currentPrice.toFixed(2)}
                {lineCards[index].planUnit}
              </p>
            )}

            {lineCards[index]?.currentPrice &&
              lineCards[index]?.currentScratchPrice &&
              lineCards[index]?.currentPrice.toFixed(2) !== lineCards[index]?.currentScratchPrice.toFixed(2) && (
                <p className='scratch-price'>
                  ${lineCards[index].currentScratchPrice.toFixed(2)}
                  {lineCards[index].planUnit}
                </p>
              )}
          </div>
        </>
      )}
      {cardState == CardState.INACTIVE && (
        <div className='add-line-container' tabIndex={0} onKeyDown={(event) => handleCardClick(event, cardState)}>
          {/*  {displayAddButton && ( */}
          <>
            <p className='add-line-text cox-text-interactive2'>Add line</p>
            <img className='plus-icon' src={getImageSrc(plusIcon)} alt={plusIconAlt} />
          </>
          {/*}   )} */}
        </div>
      )}
    </div>
  )
}

const ButtonGroup = (props: any) => {
  const {
    next,
    previous,
    nextButton = CAROUSEL_DARK_BLUE_ACTIVE_ARROW_BUTTON,
    previousButton = CAROUSEL_INACTIVE_ARROW_BUTTON,
    nextButtonAltText,
    previousButtonAltText,
    numberOfLines = 0
  } = props

  const handleNext = () => {
    next()
  }

  let showActionButton = numberOfLines >= 2

  if (window.innerWidth <= ScreenSizes.LG) {
    showActionButton = numberOfLines >= 1
  }

  if (window.innerWidth <= ScreenSizes.MD) {
    showActionButton = numberOfLines >= 0
  }

  return (
    <>
      {showActionButton && (
        <div className='carousel-button-group'>
          <ActionButton
            buttonType={ActionButtonTypes.PREVIOUS}
            isDisabled={false}
            type='button'
            onClick={() => previous()}
            aria-label={previousButtonAltText}
            //authorable icons
            carouselActiveButtonAltText={nextButtonAltText}
            carouselDisabledButtonAltText={previousButtonAltText}
            nextButton={nextButton}
            previousButton={previousButton}
          />
          <ActionButton
            buttonType={ActionButtonTypes.NEXT}
            isDisabled={false}
            type='button'
            onClick={() => handleNext()}
            aria-label={nextButtonAltText}
            //authorable icons
            carouselActiveButtonAltText={nextButtonAltText}
            carouselDisabledButtonAltText={previousButtonAltText}
            nextButton={nextButton}
            previousButton={previousButton}
          />
        </div>
      )}
    </>
  )
}

export default ReactMixAndMatchCarousel
